import { QueryClient } from '@tanstack/react-query'

/**
 * THE app's single QueryClient, and it lives here rather than in App.tsx so
 * that the auth STORES can reach it.
 *
 * It was a module-local `const` in App.tsx, which meant `logout()` had no way
 * to touch the cache - and exporting it from App.tsx was not an option,
 * because App.tsx already imports `useLanguageStore` from `../store`, so the
 * store importing App.tsx back would be a cycle. This module imports nothing
 * but the library, so every consumer - App.tsx, store/index.ts,
 * store/adminStore.ts, store/supplierStore.ts - can depend on it freely.
 *
 * `retry: 2` and `refetchOnWindowFocus: false` are moved verbatim from
 * App.tsx; nothing about the client's behaviour changed with the move.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false
    }
  }
})

/**
 * Drop every cached response. Called from all three sign-out paths.
 *
 * THIS IS THE BACKSTOP HALF of the cross-account fix, and it is the half that
 * cannot be partially wrong: it does not care whether every private key was
 * scoped to its owner. Scoping the keys (see lib/queryKeys.ts) is what
 * protects a second tab, a token expiry, or any future path that ends a
 * session without routing through a store's own sign-out - but a key someone
 * forgets to scope tomorrow is still contained by this call today.
 *
 * `clear()`, not `removeQueries()` with a filter: a sign-out is not a
 * selective event. A customer, an admin and a partner can all be signed in in
 * ONE browser at once (three stores, three tokens, three localStorage keys),
 * and any one of them leaving is a hand-over of the machine as far as we can
 * tell. Everything still on screen refetches; nothing on screen is another
 * person's data.
 *
 * Deliberately NOT a full page reload. That was considered in the spec and
 * rejected: it is equally blunt but it throws away the store's own careful
 * teardown - the cart, the guest BroxSpecs draft - and makes logout feel like
 * a crash.
 */
export const clearQueryCache = () => {
  queryClient.clear()
}
